import React, { useState } from "react";
import axios from "axios";
import { X, Router, Wifi, Loader2, CheckCircle2, AlertCircle, ShieldCheck, Globe, Network } from "lucide-react";

export const AddMikrotikRouterModal = ({ onClose, onSuccess, initialData = null }) => {
  const isEdit = !!initialData;
  const [form, setForm] = useState({
    nama: initialData?.nama || "",
    host: initialData?.host || "",
    port: initialData?.port || 8728,
    username: initialData?.username || "",
    password: "",
    use_ssl: initialData?.use_ssl || false,
    connection_type: initialData?.connection_type || "public_ip",
    catatan: initialData?.catatan || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSsl = (v) => {
    setField("use_ssl", v);
    setField("port", v ? 8729 : 8728);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess(null);
    setLoading(true);
    try {
      // Build payload; in edit mode, only send password if user filled a new one
      const payload = { ...form, port: parseInt(form.port) };
      if (isEdit && !form.password) {
        delete payload.password;
      }
      const res = isEdit
        ? await axios.patch(
            `${BACKEND_URL}/api/jaringan/mikrotik/routers/${initialData.id}`,
            payload,
            { withCredentials: true }
          )
        : await axios.post(
            `${BACKEND_URL}/api/jaringan/mikrotik/routers`,
            payload,
            { withCredentials: true }
          );
      setSuccess(res.data);
      setTimeout(() => {
        onSuccess?.(res.data.router);
        onClose();
      }, 1800);
    } catch (err) {
      setError(err.response?.data?.detail || `Gagal ${isEdit ? "memperbarui" : "menambah"} router`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="add-router-modal"
    >
      <div className="w-full max-w-lg rounded-2xl bg-[#0e1628] border border-slate-800 shadow-2xl">
        {/* Header */}
        <div className="p-5 border-b border-slate-800 flex justify-between items-start">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/20 text-blue-400 flex items-center justify-center">
              <Router className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-white">{isEdit ? "Edit Router Mikrotik" : "Tambah Router Mikrotik"}</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                Support RouterOS v6.43+ & v7. Konek via IP publik atau VPN.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white flex items-center justify-center"
            data-testid="close-add-router-modal"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Success message */}
          {success && (
            <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs flex items-start gap-2" data-testid="add-router-success">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-bold">{success.message}</p>
                <p className="text-emerald-400/80 mt-1 font-mono text-[11px]">
                  Identity: {success.test_result?.identity} • {success.test_result?.board_name} • RouterOS {success.test_result?.version}
                </p>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs flex items-start gap-2" data-testid="add-router-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {/* Nama */}
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1.5">Nama Router</label>
            <input
              type="text"
              required
              value={form.nama}
              onChange={(e) => setField("nama", e.target.value)}
              placeholder="Contoh: Core Router Pusat"
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-500"
              data-testid="router-nama-input"
            />
          </div>

          {/* Tipe koneksi */}
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1.5">Tipe Koneksi</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setField("connection_type", "public_ip")}
                className={`p-2.5 rounded-xl border text-left transition-colors ${
                  form.connection_type === "public_ip"
                    ? "border-cyan-500 bg-cyan-500/10 text-cyan-300"
                    : "border-slate-700 bg-slate-900 text-slate-400 hover:border-slate-600"
                }`}
                data-testid="conn-type-public"
              >
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  <span className="text-xs font-bold">IP Publik</span>
                </div>
                <p className="text-[10px] mt-0.5 text-slate-500">Direct via port forwarding</p>
              </button>
              <button
                type="button"
                onClick={() => setField("connection_type", "vpn")}
                className={`p-2.5 rounded-xl border text-left transition-colors ${
                  form.connection_type === "vpn"
                    ? "border-indigo-500 bg-indigo-500/10 text-indigo-300"
                    : "border-slate-700 bg-slate-900 text-slate-400 hover:border-slate-600"
                }`}
                data-testid="conn-type-vpn"
              >
                <div className="flex items-center gap-2">
                  <Network className="w-4 h-4" />
                  <span className="text-xs font-bold">Via VPN</span>
                </div>
                <p className="text-[10px] mt-0.5 text-slate-500">Wireguard/IPsec/OVPN</p>
              </button>
            </div>
          </div>

          {/* Host & Port */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-slate-300 block mb-1.5">
                Host / IP {form.connection_type === "vpn" ? "VPN" : "Publik"}
              </label>
              <input
                type="text"
                required
                value={form.host}
                onChange={(e) => setField("host", e.target.value)}
                placeholder={form.connection_type === "vpn" ? "10.20.0.1" : "203.0.113.10"}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono focus:outline-none focus:ring-1 focus:ring-cyan-500"
                data-testid="router-host-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1.5">Port API</label>
              <input
                type="number"
                required
                value={form.port}
                onChange={(e) => setField("port", e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono focus:outline-none focus:ring-1 focus:ring-cyan-500"
                data-testid="router-port-input"
              />
            </div>
          </div>

          {/* Username & Password */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1.5">Username API</label>
              <input
                type="text"
                required
                value={form.username}
                onChange={(e) => setField("username", e.target.value)}
                placeholder="api-monitor"
                autoComplete="username"
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                data-testid="router-username-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-300 block mb-1.5">Password{isEdit && <span className="text-slate-500 font-normal"> (kosongkan jika tidak ganti)</span>}</label>
              <input
                type="password"
                required={!isEdit}
                value={form.password}
                onChange={(e) => setField("password", e.target.value)}
                autoComplete="new-password"
                placeholder={isEdit ? "•••••••• (tidak berubah)" : ""}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                data-testid="router-password-input"
              />
            </div>
          </div>

          {/* SSL Toggle */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-slate-900/60 border border-slate-800">
            <div className="flex items-center gap-2">
              <ShieldCheck className={`w-4 h-4 ${form.use_ssl ? "text-emerald-400" : "text-slate-500"}`} />
              <div>
                <p className="text-xs font-semibold text-slate-200">API-SSL (port 8729)</p>
                <p className="text-[10px] text-slate-500">Rekomendasi untuk IP publik</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={form.use_ssl}
                onChange={(e) => handleSsl(e.target.checked)}
                className="sr-only peer"
                data-testid="router-ssl-toggle"
              />
              <div className="w-10 h-5 bg-slate-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-600"></div>
            </label>
          </div>

          {/* Catatan */}
          <div>
            <label className="text-xs font-semibold text-slate-300 block mb-1.5">Catatan (opsional)</label>
            <textarea
              rows={2}
              value={form.catatan}
              onChange={(e) => setField("catatan", e.target.value)}
              placeholder="Cabang, lokasi, dsb."
              className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-cyan-500"
              data-testid="router-catatan-input"
            />
          </div>

          {/* Info */}
          <div className="p-3 rounded-xl bg-blue-500/5 border border-blue-500/20 text-[11px] text-slate-400">
            <p className="text-blue-300 font-semibold mb-1 flex items-center gap-1.5">
              <Wifi className="w-3 h-3" /> Persyaratan Router
            </p>
            <ul className="space-y-0.5 list-disc list-inside">
              <li>Service /ip service/api atau api-ssl <strong>enabled</strong></li>
              <li>Firewall: izinkan port {form.use_ssl ? "8729" : "8728"} dari IP server ini</li>
              <li>User RouterOS dengan policy minimum: <code className="text-cyan-400">read, write, api, test</code></li>
              <li>Sistem akan test koneksi dulu sebelum menyimpan</li>
            </ul>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t border-slate-800">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 py-2 rounded-xl text-xs font-bold text-slate-300 bg-slate-800 hover:bg-slate-700 disabled:opacity-50"
              data-testid="cancel-add-router-btn"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={loading || !!success}
              className="flex-1 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-cyan-600/30"
              data-testid="submit-add-router-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Testing koneksi...</span>
                </>
              ) : success ? (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Berhasil</span>
                </>
              ) : (
                <>
                  <Wifi className="w-4 h-4" />
                  <span>{isEdit ? "Test & Update Router" : "Test & Simpan Router"}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
